import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useState } from 'react';
import {
  LayerGroup,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';
import { useLocation } from 'react-router-dom';
import env from '../../config/env';
import AnnonceItem2 from '../AnnonceItem2';

import './MapItem.css';

/* ------------------------------------------------------------------
   FOND DE CARTE
   CARTO exige maintenant une clé API (gratuite) sur ses tuiles raster :
   sans clé, chaque tuile affiche le filigrane "API KEY REQUIRED".

------------------------------------------------------------------- */
const CARTO_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

/* Accepte "mapbox://styles/user/id" (copié depuis Studio) ou directement "user/id" */
const MAPBOX_STYLE = (env.mapboxStyle || '').replace(/^mapbox:\/\/styles\//, '');

const getTileConfig = () => {
  if (MAPBOX_STYLE && env.mapboxToken2) {
    return {
      url: `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/tiles/256/{z}/{x}/{y}@2x?access_token=${env.mapboxToken2}`,
      attribution:
        '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 20,
    };
  }

  if (env.cartoApiKey) {
    return {
      url: `https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=${env.cartoApiKey}`,
      attribution: CARTO_ATTRIBUTION,
      subdomains: 'abcd',
      maxZoom: 20,
    };
  }

  return {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
    maxZoom: 19,
    maxNativeZoom: 16,
  };
};

const TILES = getTileConfig();

/* ------------------------------------------------------------------
   MARQUEURS : pastille de prix. Les icônes sont mises en cache pour
   éviter de recréer (et faire clignoter) chaque marqueur à chaque rendu.
------------------------------------------------------------------- */
const iconCache = new Map();

const getPinIcon = (price, active) => {
  const key = `${price}-${active}`;

  if (!iconCache.has(key)) {
    iconCache.set(
      key,
      L.divIcon({
        className: 'mc-pin-wrapper',
        iconSize: [0, 0],
        iconAnchor: [0, 0],
        html: `<div class="mc-pin${active ? ' is-active' : ''}">
                 <i class="bi bi-car-front-fill"></i>${price}€
               </div>`,
      })
    );
  }

  return iconCache.get(key);
};

const RecenterAutomatically = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    map.setView([lat, lng], 12);
  }, [lat, lng, map]);

  return null;
};

/* Un clic sur la carte referme la bulle ouverte */
const CloseOnMapClick = ({ onClose }) => {
  useMapEvents({ click: onClose });
  return null;
};

const MapItem = ({ centers, centercoords, Ischanged, onAjouterDeman }) => {
  const zoom = 16;

  const location = useLocation();
  const isSearchPage = location.pathname.includes('search');

  const [coordonatesCenter, setCoordonatesCenter] = useState(centers);
  const [openMarkerId, setOpenMarkerId] = useState(null);

  const handleClick = (id) => {
    setOpenMarkerId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    setCoordonatesCenter(centers);
  }, [Ischanged, centers, centercoords, location.pathname]);

  return (
    <MapContainer
      center={centercoords}
      zoom={zoom}
      className={`mc-map${isSearchPage ? ' mc-map--full' : ''}`}
      scrollWheelZoom={false}
      zoomControl={false}
    >
      <TileLayer {...TILES} />

      <ZoomControl position="bottomright" />

      <RecenterAutomatically lat={parseFloat(centercoords[0])} lng={parseFloat(centercoords[1])} />

      <CloseOnMapClick onClose={() => setOpenMarkerId(null)} />

      <LayerGroup>
        {coordonatesCenter &&
          coordonatesCenter?.map((center) => {
            const [latStr, lngStr] = center?.locationCoordinates?.split(',') ?? [];
            const lat = parseFloat(latStr);
            const lng = parseFloat(lngStr);
            const isOpen = openMarkerId === center?.vehiculeAnnonceId;

            return (
              <Marker
                key={center?.vehiculeAnnonceId}
                position={[lat, lng]}
                icon={getPinIcon(center?.reservationPrice, isOpen)}
                zIndexOffset={isOpen ? 1000 : 0}
                eventHandlers={{
                  click: () => handleClick(center?.vehiculeAnnonceId),
                }}
              >
                {isOpen && (
                  <Tooltip
                    permanent
                    interactive
                    direction="top"
                    offset={[0, -46]}
                    className="mc-map-tooltip"
                  >
                    <div style={{ width: 260 }}>
                      <AnnonceItem2 propsData={center} onAjouter={onAjouterDeman} />
                    </div>
                  </Tooltip>
                )}
              </Marker>
            );
          })}
      </LayerGroup>
    </MapContainer>
  );
};

export default MapItem;
